import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gavel, Search, User as UserIcon, LogOut, Shield, ChevronDown, Menu, X, Coins } from "lucide-react";
import { useCurrency } from "../lib/currency";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

function CurrencyPicker() {
  const { currency, setCurrency, supported } = useCurrency();
  return (
    <Select value={currency} onValueChange={setCurrency}>
      <SelectTrigger className="h-8 w-[78px] text-xs font-bold" data-testid="header-currency-select" title="Display currency (you are charged in INR)"><SelectValue /></SelectTrigger>
      <SelectContent>{supported.map(c => <SelectItem key={c} value={c} data-testid={`currency-option-${c}`}>{c}</SelectItem>)}</SelectContent>
    </Select>
  );
}
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const PRIMARY_SLUGS = ["property", "businesses-for-sale", "cars", "bikes-scooters"];

export default function Header() {
  const { user, logout } = useAuth();
  const isAdmin = user?.roles?.includes("admin");
  const [cats, setCats] = useState({ primary: [], more: [] });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bidzBal, setBidzBal] = useState(null);

  useEffect(() => {
    if (!user) { setBidzBal(null); return undefined; }
    const load = () => api.get("/bidz/wallet").then(r => setBidzBal({ wallet: r.data.general_balance, passes: r.data.active_pass_count })).catch(() => {});
    load();
    window.addEventListener("bidz:changed", load);
    return () => window.removeEventListener("bidz:changed", load);
  }, [user]);

  useEffect(() => {
    api.get("/categories/tree").then(r => {
      const primary = r.data.filter(c => c.primary && PRIMARY_SLUGS.includes(c.slug));
      const more = r.data.filter(c => !c.primary);
      setCats({ primary, more });
    });
  }, []);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 dark:bg-slate-950/85 border-b border-slate-200/80 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-4">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group flex-shrink-0">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-amber-400 shadow-lg group-hover:scale-105 transition">
            <Gavel className="h-5 w-5" />
          </span>
          <span className="font-extrabold text-lg tracking-tight text-slate-900 dark:text-white">
            Auction<span className="text-amber-500">Bulls</span>
          </span>
        </Link>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const q = new FormData(e.currentTarget).get("q");
            window.location.href = `/auctions?q=${encodeURIComponent(q || "")}`;
          }}
          className="hidden md:flex flex-1 max-w-xs relative"
        >
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            name="q"
            data-testid="header-search-input"
            placeholder="Search auctions..."
            className="w-full pl-9 pr-4 py-2 rounded-full border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </form>

        <nav className="hidden xl:flex items-center gap-1 text-sm font-medium">
          <Link data-testid="header-nav-auctions" to="/auctions" className="px-2.5 py-2 hover:text-amber-600">Auctions</Link>
          {cats.primary.map(c => (
            <Link
              key={c.id}
              data-testid={`header-nav-${c.slug}`}
              to={`/auctions?category_id=${c.id}`}
              className="px-2.5 py-2 hover:text-amber-600 whitespace-nowrap"
            >
              {c.name}
            </Link>
          ))}
          <Link data-testid="header-nav-bidz" to="/bidz" className="px-2.5 py-2 hover:text-amber-600">Bidz</Link>
          <Link data-testid="header-nav-blog" to="/blog" className="px-2.5 py-2 hover:text-amber-600">Blog</Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="header-nav-more" className="px-2.5 py-2 hover:text-amber-600 inline-flex items-center gap-1">
                More <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs">More Categories</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {cats.more.map(c => (
                <DropdownMenuItem key={c.id} asChild>
                  <Link to={`/auctions?category_id=${c.id}`}>{c.name}</Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild><Link to="/sell">Sell an Item</Link></DropdownMenuItem>
              <DropdownMenuItem asChild><Link to="/how-it-works">How It Works</Link></DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link data-testid="header-nav-sell" to="/sell" className="px-2.5 py-2 hover:text-amber-600 font-semibold">Sell</Link>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <CurrencyPicker />
          {!user ? (
            <>
              <Link to="/signin"><Button data-testid="header-nav-signin" variant="ghost" size="sm">Sign In</Button></Link>
              <Link to="/signup"><Button data-testid="header-nav-signup" size="sm" className="hidden sm:inline-flex bg-slate-900 hover:bg-slate-800 text-white">Create Account</Button></Link>
            </>
          ) : (
            <>
            <Link to="/buyer?tab=bidz" data-testid="header-bidz-balance" title={bidzBal ? `Wallet: ${bidzBal.wallet} Bidz · Active auction passes: ${bidzBal.passes}` : "Bidz wallet"} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 px-2.5 py-1 text-xs font-bold hover:bg-amber-200 transition-colors">
              <Coins className="h-3.5 w-3.5" /> {bidzBal ? bidzBal.wallet : "–"}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="header-nav-dashboard" variant="ghost" size="sm" className="gap-2">
                  <UserIcon className="h-4 w-4" /> <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link data-testid="menu-buyer-dash" to="/buyer">Buyer Dashboard</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link data-testid="menu-seller-dash" to="/seller">Seller Dashboard</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link data-testid="menu-bidz-wallet" to="/buyer?tab=bidz">Bidz Wallet</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link data-testid="menu-watchlist" to="/watchlist">Watchlist</Link></DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link data-testid="menu-admin" to="/admin" className="text-amber-600 font-semibold">
                        <Shield className="h-4 w-4 mr-2 inline" /> Admin Console
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-signout" className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
          <button onClick={() => setMobileOpen(o => !o)} className="xl:hidden ml-1 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Menu" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-2 gap-1 text-sm">
            <Link onClick={() => setMobileOpen(false)} to="/auctions" className="px-3 py-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800">Auctions</Link>
            <Link onClick={() => setMobileOpen(false)} to="/sell" className="px-3 py-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold">Sell</Link>
            {cats.primary.map(c => (
              <Link key={c.id} onClick={() => setMobileOpen(false)} to={`/auctions?category_id=${c.id}`} className="px-3 py-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800">{c.name}</Link>
            ))}
            <Link onClick={() => setMobileOpen(false)} to="/blog" className="px-3 py-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800">Blog</Link>
            <Link onClick={() => setMobileOpen(false)} to="/how-it-works" className="px-3 py-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800 col-span-2">How It Works</Link>
          </div>
        </div>
      )}
    </header>
  );
}
